import { type ButtonHTMLAttributes, forwardRef, type ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { Skeleton } from '../feedback/Loading'
import { DropdownMenu, type MenuItem } from '../overlay/Menu'
import { useIsBelowMd } from '../useMediaQuery'

// DataTable — tabular sets that sort, filter and paginate (tenants, audit)
// (design/components/data-table.html). Real <table> semantics with <th scope>,
// aria-sort on the active column, keyboard-activable sort headers, mixed-state
// select-all, loading skeleton rows (not a spinner) and an empty slot. Numbers
// are tabular and right-aligned. For column-less objects use List instead.
//
// Below md (phone) the columnar table is dropped for a stacked card list: the
// first column becomes the card title, the rest become label/value rows, and
// selection + row actions carry over. A wide table scrolling sideways on a
// phone hides half its columns; cards show each row whole. See tablet-
// responsive.md — table on desktop, cards on phone.

// Default cell rendering only coerces primitives; objects must use col.render.
function defaultCell(value: unknown): ReactNode {
  if (typeof value === 'string' || typeof value === 'number') return value
  return ''
}

export interface DataTableColumn<Row> {
  key: string
  label: string
  align?: 'left' | 'right'
  sortable?: boolean
  render?: (row: Row) => ReactNode
}

export interface DataTableProps<Row extends { id: string }> {
  columns: DataTableColumn<Row>[]
  rows: Row[]
  sort?: { key: string; dir: 'asc' | 'desc' }
  onSort?: (key: string) => void
  selectable?: boolean
  selectedIds?: string[]
  onSelect?: (ids: string[]) => void
  /** Accessible name for a row's select checkbox. Defaults to the first column's value. */
  getRowLabel?: (row: Row) => string
  rowActions?: (row: Row) => MenuItem[]
  loading?: boolean
  loadingRows?: number
  empty?: ReactNode
  /** Accessible name for the table. */
  caption: string
  className?: string
}

function TableCheckbox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean
  indeterminate?: boolean
  onChange: (checked: boolean) => void
  label: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate)
  }, [indeterminate])
  return (
    <span className="relative inline-flex align-middle">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
        className="peer size-[15px] cursor-pointer appearance-none rounded-[4px] border-[1.5px] border-input-line bg-surface checked:border-brand-blue checked:bg-brand-blue indeterminate:border-brand-blue indeterminate:bg-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
      />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 hidden size-[9px] -translate-x-1/2 -translate-y-1/2 text-white peer-checked:block"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 hidden h-0.5 w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-px bg-white peer-indeterminate:block"
      />
    </span>
  )
}

function SortArrow({ dir }: { dir: 'asc' | 'desc' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="ml-[5px] inline-block size-[11px] align-middle text-link"
    >
      <path d={dir === 'asc' ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6'} />
    </svg>
  )
}

// forwardRef so Radix's <DropdownMenu.Trigger asChild> can attach its ref/props
const MenuTrigger = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function MenuTrigger(props, ref) {
    const { t } = useTranslation('components')
    return (
      <button
        ref={ref}
        type="button"
        aria-label={t('ui.dataTable.actions')}
        className="inline-flex size-7 items-center justify-center rounded-md text-text-3 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue [&_svg]:size-[18px]"
        {...props}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="5" cy="12" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
    )
  },
)

/**
 * Sortable/selectable table with real <table> semantics (th scope, aria-sort,
 * mixed-state select-all), skeleton loading rows and an `empty` slot. Below md
 * it re-renders as stacked cards: first column = title, rest = label/value
 * rows. Select-all toggles only this page's rows, preserving any cross-page
 * selection.
 */
export function DataTable<Row extends { id: string }>({
  columns,
  rows,
  sort,
  onSort,
  selectable,
  selectedIds = [],
  onSelect,
  getRowLabel,
  rowActions,
  loading,
  loadingRows = 5,
  empty,
  caption,
  className,
}: DataTableProps<Row>) {
  const { t } = useTranslation('components')
  const compact = useIsBelowMd()
  const selectedSet = new Set(selectedIds)
  const allIds = rows.map((r) => r.id)
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id))
  const someSelected = allIds.some((id) => selectedSet.has(id)) && !allSelected
  const totalCols = (selectable ? 1 : 0) + columns.length + (rowActions ? 1 : 0)
  const firstKey = columns[0]?.key
  const primary = columns[0]
  const secondary = columns.slice(1)

  function cellOf(col: DataTableColumn<Row>, row: Row): ReactNode {
    return col.render ? col.render(row) : defaultCell((row as Record<string, unknown>)[col.key])
  }

  function rowLabel(row: Row): string {
    if (getRowLabel) return getRowLabel(row)
    const v = firstKey ? (row as Record<string, unknown>)[firstKey] : undefined
    return t('ui.dataTable.selectRow', {
      label: typeof v === 'string' || typeof v === 'number' ? v : row.id,
    })
  }

  function toggleAll(checked: boolean) {
    // Deselect removes only this page's rows, preserving any cross-page selection.
    onSelect?.(
      checked
        ? [...selectedIds, ...allIds.filter((id) => !selectedSet.has(id))]
        : selectedIds.filter((id) => !allIds.includes(id)),
    )
  }
  function toggleRow(id: string, checked: boolean) {
    onSelect?.(checked ? [...selectedIds, id] : selectedIds.filter((x) => x !== id))
  }

  // Phone: stacked cards instead of a sideways-scrolling table.
  if (compact) {
    return (
      <div className={cn('flex flex-col gap-2.5', className)}>
        {loading ? (
          Array.from({ length: loadingRows }, (_, i) => (
            <div key={i} className="rounded-lg border border-line bg-surface p-3.5">
              <Skeleton variant="line" width="55%" />
              <div className="mt-3 flex flex-col gap-2">
                {secondary.map((col) => (
                  <Skeleton key={col.key} variant="line" width="40%" />
                ))}
              </div>
            </div>
          ))
        ) : rows.length === 0 ? (
          (empty ?? (
            <div className="rounded-lg border border-line bg-surface px-5 py-10 text-center text-[12px] text-text-2">
              {t('ui.dataTable.empty')}
            </div>
          ))
        ) : (
          <>
            {selectable && (
              <label className="flex items-center gap-2.5 px-1 py-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-3">
                <TableCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  label={t('ui.dataTable.selectAll')}
                />
                {t('ui.dataTable.selectAll')}
              </label>
            )}
            <ul role="list" aria-label={caption} className="flex flex-col gap-2.5">
              {rows.map((row) => {
                const selected = selectedSet.has(row.id)
                return (
                  <li
                    key={row.id}
                    className={cn(
                      'rounded-lg border bg-surface p-3.5 transition-colors',
                      selected
                        ? 'border-brand-blue bg-[color-mix(in_srgb,var(--brand-blue)_5%,transparent)]'
                        : 'border-line',
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      {selectable && (
                        <span className="pt-0.5">
                          <TableCheckbox
                            checked={selected}
                            onChange={(c) => toggleRow(row.id, c)}
                            label={rowLabel(row)}
                          />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 text-[13px] text-text">
                            {primary ? cellOf(primary, row) : null}
                          </div>
                          {rowActions && (
                            <span className="-mr-1 -mt-1 flex-none">
                              <DropdownMenu trigger={<MenuTrigger />} items={rowActions(row)} />
                            </span>
                          )}
                        </div>
                        {secondary.length > 0 && (
                          <dl className="mt-2.5 flex flex-col gap-1.5">
                            {secondary.map((col) => (
                              <div
                                key={col.key}
                                className="flex items-center justify-between gap-3"
                              >
                                <dt className="flex-none text-[10px] font-bold uppercase tracking-[0.06em] text-text-3">
                                  {col.label}
                                </dt>
                                <dd className="flex min-w-0 items-center justify-end gap-1.5 text-right text-[12.5px] text-text">
                                  {cellOf(col, row)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>
    )
  }

  return (
    <div className={cn('overflow-x-auto rounded-lg border border-line bg-surface', className)}>
      {/* min-width keeps columns legible on tablet: the table scrolls
          horizontally inside the bordered card instead of squishing. */}
      <table className="w-full min-w-[44rem] border-collapse">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {selectable && (
              <th scope="col" className="w-10 border-b border-line bg-surface-2 py-2.5 pl-[14px]">
                <TableCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  label={t('ui.dataTable.selectAll')}
                />
              </th>
            )}
            {columns.map((col) => {
              const active = sort?.key === col.key
              const ariaSort = active
                ? sort.dir === 'asc'
                  ? 'ascending'
                  : 'descending'
                : col.sortable
                  ? 'none'
                  : undefined
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSort}
                  className={cn(
                    'border-b border-line bg-surface-2 px-[14px] py-2.5 text-[10px] font-bold uppercase tracking-[0.06em] text-text-3 whitespace-nowrap',
                    col.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort?.(col.key)}
                      className="inline-flex items-center font-bold uppercase tracking-[0.06em] transition-colors hover:text-text-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
                    >
                      {col.label}
                      {active && <SortArrow dir={sort.dir} />}
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              )
            })}
            {rowActions && (
              <th scope="col" className="w-[46px] border-b border-line bg-surface-2 py-2.5">
                <span className="sr-only">{t('ui.dataTable.actions')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="[&_tr:last-child>td]:border-b-0">
          {loading ? (
            Array.from({ length: loadingRows }, (_, i) => (
              <tr key={i}>
                {selectable && <td className="h-12 border-b border-line pl-[14px]" />}
                {columns.map((col, ci) => (
                  <td
                    key={col.key}
                    className={cn(
                      'h-12 border-b border-line px-[14px]',
                      col.align === 'right' && 'text-right',
                    )}
                  >
                    <Skeleton
                      variant="line"
                      width={ci === 0 ? '60%' : '40%'}
                      className="inline-block align-middle"
                    />
                  </td>
                ))}
                {rowActions && <td className="h-12 border-b border-line" />}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={totalCols} className="p-0">
                {empty ?? (
                  <div className="px-5 py-10 text-center text-[12px] text-text-2">
                    {t('ui.dataTable.empty')}
                  </div>
                )}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const selected = selectedSet.has(row.id)
              return (
                <tr
                  key={row.id}
                  className={cn(
                    'transition-colors',
                    selected
                      ? 'bg-[color-mix(in_srgb,var(--brand-blue)_5%,transparent)]'
                      : 'hover:bg-hover',
                  )}
                >
                  {selectable && (
                    <td className="h-12 border-b border-line pl-[14px] align-middle">
                      <TableCheckbox
                        checked={selected}
                        onChange={(c) => toggleRow(row.id, c)}
                        label={rowLabel(row)}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        'h-12 border-b border-line px-[14px] align-middle text-[12.5px] text-text',
                        col.align === 'right' && 'text-right tabular-nums',
                      )}
                    >
                      {col.render
                        ? col.render(row)
                        : defaultCell((row as Record<string, unknown>)[col.key])}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="h-12 border-b border-line text-center align-middle">
                      <DropdownMenu trigger={<MenuTrigger />} items={rowActions(row)} />
                    </td>
                  )}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
